import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthRedirect } from '../utils/authRedirect';

const UserContext = createContext({
    user: null,
    loading: false,
    error: '',
    refreshUser: async () => {},
    updateUser: () => {},
    resetUser: () => {},
});

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const handleAuthError = useAuthRedirect();

    const fetchUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setUser(null);
            setLoading(false);
            setError('');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/auth/me', {
                headers: {
                    'x-auth-token': token,
                },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to load user profile.');
            }

            const data = await response.json();
            setUser(data);
            // Sync server-stored ad consent to localStorage so client ad components can read it immediately
            if (typeof window !== 'undefined' && data && (data.adConsent === 'granted' || data.adConsent === 'denied')) {
                try { localStorage.setItem('ads_consent', data.adConsent); } catch (e) {}
            }
        } catch (err) {
            setError(err.message);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [handleAuthError]);

    const updateUser = useCallback((updates) => {
        setUser(prev => (prev ? { ...prev, ...updates } : prev));
    }, []);

    const resetUser = useCallback(() => {
        setUser(null);
        setLoading(false);
        setError('');
    }, []);

    useEffect(() => {
        fetchUser();

        // Refresh user profile when window regains focus or becomes visible
        const onFocus = () => fetchUser();
        const onVisibility = () => { if (document.visibilityState === 'visible') fetchUser(); };
        if (typeof window !== 'undefined') {
            window.addEventListener('focus', onFocus);
            document.addEventListener('visibilitychange', onVisibility);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('focus', onFocus);
                document.removeEventListener('visibilitychange', onVisibility);
            }
        };
    }, [fetchUser]);

    const value = {
        user,
        loading,
        error,
        refreshUser: fetchUser,
        updateUser,
        resetUser,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
